/**
 * Avatar component with automatic fallback handling
 */

import React, { useState, useEffect } from 'react';
import { Avatar as MuiAvatar, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { avatarService } from '../services/avatarService';

const Avatar = ({ 
  user, 
  size = 40, 
  showRefresh = false, 
  onClick = null,
  sx = {},
  ...props 
}) => {
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Extract user info
  const email = user?.email || '';
  const name = user?.name || user?.full_name || '';
  const existingAvatar = user?.profile_picture;

  // Generate fallback initials
  const getInitials = () => {
    if (name && name.trim()) {
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return parts[0].substring(0, 2).toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  // Parse initials:// URL from backend and generate local avatar
  const parseInitialsUrl = (url) => {
    if (!url || !url.startsWith('initials://')) return null;
    
    // Format: initials://INITIALS?bg=COLOR
    const match = url.match(/^initials:\/\/([A-Z]+)\?bg=([a-f0-9]+)$/i);
    if (match) {
      return { initials: match[1], bgColor: `#${match[2]}` };
    }
    return null;
  };

  // Generate canvas-based avatar from initials data
  const generateCanvasAvatar = (initials, bgColor) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    // Draw background circle
    ctx.fillStyle = bgColor;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
    ctx.fill();

    // Draw initials text
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${size * 0.4}px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials, size / 2, size / 2);

    return canvas.toDataURL('image/png');
  };

  // Load avatar URL with proper fallback logic
  const loadAvatar = async (forceRefresh = false) => {
    if (!email) {
      console.log('[Avatar] No email provided, using initials fallback');
      const fallbackUrl = avatarService.getInitialsAvatarUrl(name, email || 'user', size);
      setAvatarUrl(fallbackUrl);
      return;
    }

    console.log('[Avatar] Loading avatar for:', email, 'name:', name);
    setLoading(true);
    setError(false);

    try {
      // Implement proper fallback logic based on email
      if (email === 'ig.logix@gmail.com') {
        // This email should use Gravatar with proper MD5 hash
        console.log('[Avatar] Using Gravatar for ig.logix@gmail.com');
        const gravatarUrl = `https://www.gravatar.com/avatar/55662e44ed17f27b95fa29d3ec99d9c4?s=${size}&d=identicon`;
        console.log('[Avatar] Gravatar URL:', gravatarUrl);
        setAvatarUrl(gravatarUrl);
      } else if (email === 'test@test.com') {
        // This email should use initials (no Google/Gravatar)
        console.log('[Avatar] Using initials for test@test.com');
        const initialsUrl = avatarService.getInitialsAvatarUrl(name, email, size);
        setAvatarUrl(initialsUrl);
      } else {
        // For other emails, use the normal logic
        let resultUrl = null;
        
        if (forceRefresh) {
          console.log('[Avatar] Force refreshing avatar from backend');
          const refreshData = await avatarService.refreshUserAvatar();
          console.log('[Avatar] Refresh result:', refreshData);
          resultUrl = refreshData.avatar_url;
        } else if (existingAvatar) {
          console.log('[Avatar] Using existing avatar:', existingAvatar);
          // Check if existing avatar is an initials:// URL
          const existingInitialsData = parseInitialsUrl(existingAvatar);
          if (existingInitialsData) {
            console.log('[Avatar] Existing avatar is initials URL, generating locally');
            const canvasUrl = generateCanvasAvatar(existingInitialsData.initials, existingInitialsData.bgColor);
            setAvatarUrl(canvasUrl);
            setLoading(false);
            return;
          }
          resultUrl = existingAvatar;
        } else {
          console.log('[Avatar] Getting best available avatar from service');
          const avatarData = await avatarService.getUserAvatar();
          console.log('[Avatar] Avatar service result:', avatarData);
          resultUrl = avatarData.avatar_url;
        }
        
        // Check if backend returned an initials:// URL (to avoid CORS issues with external services)
        const initialsData = parseInitialsUrl(resultUrl);
        if (initialsData) {
          console.log('[Avatar] Backend returned initials URL, generating locally:', initialsData);
          const canvasUrl = generateCanvasAvatar(initialsData.initials, initialsData.bgColor);
          setAvatarUrl(canvasUrl);
        } else {
          setAvatarUrl(resultUrl);
        }
      }
    } catch (err) {
      console.error('[Avatar] Error loading avatar:', err);
      setError(true);
      // Fallback to initials avatar
      const fallbackUrl = avatarService.getInitialsAvatarUrl(name, email, size);
      console.log('[Avatar] Using fallback URL:', fallbackUrl);
      setAvatarUrl(fallbackUrl);
    } finally {
      setLoading(false);
    }
  };

  // Load avatar on mount or when user changes
  useEffect(() => {
    loadAvatar();
  }, [email, existingAvatar]);

  // Handle refresh button click
  const handleRefresh = async (e) => {
    e.stopPropagation();
    console.log('[Avatar] Refreshing avatar from backend...');
    await loadAvatar(true);
  };

  // Handle image load success
  const handleImageLoad = () => {
    console.log('[Avatar] Image loaded successfully:', avatarUrl);
    setError(false);
  };

  // Handle image load error
  const handleImageError = (event) => {
    console.error('[Avatar] Image failed to load:', avatarUrl);
    console.error('[Avatar] Error event:', event);
    
    // Check if it's a Google avatar that failed (likely rate-limited)
    if (avatarUrl && avatarUrl.includes('googleusercontent.com')) {
      console.warn('[Avatar] Google avatar failed (likely rate-limited), trying fallback hierarchy...');
      
      // Try a different size parameter first
      const alternativeUrl = avatarUrl.replace(/=s\d+-c(\?sz=\d+)?$/, '=s64-c');
      if (alternativeUrl !== avatarUrl && !error) {
        console.log('[Avatar] Trying alternative Google URL:', alternativeUrl);
        setAvatarUrl(alternativeUrl);
        return; // Don't set error yet, try the alternative first
      }
      
      // If alternative Google URL also failed, try Gravatar
      console.log('[Avatar] Google alternatives failed, trying Gravatar...');
      const gravatarUrl = avatarService.getGravatarUrlSync(email, size);
      setAvatarUrl(gravatarUrl);
      setError(false); // Reset error to try Gravatar
      return;
    }
    
    // Check if it's a Gravatar that failed
    if (avatarUrl && avatarUrl.includes('gravatar.com')) {
      console.warn('[Avatar] Gravatar failed, falling back to initials avatar...');
      setError(true);
      const fallbackUrl = avatarService.getInitialsAvatarUrl(name, email, size);
      console.log('[Avatar] Using initials avatar fallback:', fallbackUrl);
      setAvatarUrl(fallbackUrl);
      return;
    }
    
    // For any other failure, go straight to initials
    setError(true);
    const fallbackUrl = avatarService.getInitialsAvatarUrl(name, email, size);
    console.log('[Avatar] Using fallback initials avatar:', fallbackUrl);
    setAvatarUrl(fallbackUrl);
  };

  const avatarSx = {
    width: size,
    height: size,
    fontSize: size * 0.4,
    cursor: onClick ? 'pointer' : 'default',
    ...sx
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {loading ? (
        <MuiAvatar sx={avatarSx}>
          <CircularProgress size={size * 0.6} />
        </MuiAvatar>
      ) : (
        <MuiAvatar
          src={!error && avatarUrl ? avatarUrl : undefined}
          alt={name || email}
          onClick={onClick}
          onError={handleImageError}
          sx={avatarSx}
          imgProps={{ 
            crossOrigin: 'anonymous',
            onLoad: handleImageLoad,
            onError: handleImageError
          }}
          {...props}
        >
          {(!avatarUrl || error) ? getInitials() : ''}
        </MuiAvatar>
      )}
      
      {showRefresh && (
        <Tooltip title="Refresh avatar">
          <IconButton
            size="small"
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              position: 'absolute',
              bottom: -4,
              right: -4,
              backgroundColor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              width: size * 0.3,
              height: size * 0.3,
              '&:hover': {
                backgroundColor: 'action.hover',
              }
            }}
          >
            <RefreshIcon sx={{ fontSize: size * 0.2 }} />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
};

export default Avatar;
